import { z } from 'zod'
import { formatPrice } from '../../../utils/formatPrice'
import { useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from 'react-query'
import { api } from '../../../services/api'
import { Flex, InputGroup, InputLeftAddon, Spinner, useToast } from '@chakra-ui/react'
import { Select } from './components/Select'
import Input from '../../../components/Input'
import React, { useCallback, useEffect, useState } from 'react'
import { Button } from '../../../components/Button'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useOrcamentoPedido } from '../../../contexts/OrcamentoPedidoProvider'
import { Multiplo } from './components/Modais/Multiplo'
import { ConfirmDialog } from './components/Alerts/ConfirmDialog'
import { MdOutlineWarning } from 'react-icons/md'

const schema = z.object({
  cor: z.number(),
  personalizacao: z.number(),
  impressao: z
    .string()
    .nonempty()
    .refine((value) => parseInt(value) > 0, {
      path: ['impressao'],
      message: 'Impressão deve ser maior que zero',
    }),
  prazo: z.number().int().min(1),
  quantidade: z
    .string()
    //nao deixa por '.' e ',' no campo quantidade
    .regex(/^[0-9]*$/)

    .transform((value) => {
      return parseInt(value)
    }),
})

async function updateProductInfo({ numero_nota, codigo_parceiro, email_parceiro, telefone_parceiro, nome_parceiro, codigo_vendedor, produtos }) {
  const response = await api.put('/site/v2/orcamento-crm/inserir-produtos-orcamento', {
    numero_nota,
    codigo_parceiro,
    email_parceiro,
    telefone_parceiro,
    nome_parceiro,
    codigo_vendedor,
    produtos,
  })

  return response.data
}

export function FormUpdateItem({ item, cliente, email_parceiro, telefone_parceiro, codigo_vendedor, nome_parceiro }) {
  const { numeroProposta: numProposta } = useParams()
  const { recalculateProductPrice } = useOrcamentoPedido()
  const [isAlertMessageQuantityOpen, setIsAlertMessageQuantityOpen] = useState(false)
  const [pendingData, setPendingData] = useState(null)
  const [quantidadeUltraRapidoSelecionada, setQuantidadeUltraRapidoSelecionada] = useState(0)

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
  })

  const queryClient = useQueryClient()
  const toast = useToast({
    position: 'top-right',
    isClosable: true,
    duration: 2000,
  })

  const cor = watch('cor')
  const impressao = watch('impressao')
  const prazo = watch('prazo')
  const personalizacao = watch('personalizacao')

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (data) =>
      updateProductInfo({
        numero_nota: item.numero_nota,
        codigo_parceiro: cliente.codigo_parceiro,
        email_parceiro: email_parceiro,
        telefone_parceiro: telefone_parceiro,
        nome_parceiro: nome_parceiro,
        codigo_vendedor: codigo_vendedor,
        produtos: [data],
      }),

    onSuccess: (data) => {
      queryClient.setQueryData(['items', numProposta], (oldData) => {
        const updatedItem = oldData.itens.map((item) => {
          if (item.codigo_produto === data.codigo_produto && item.sequencia === data.sequencia) {
            return {
              ...item,
              ...data,
              codigo_cor: parseInt(data.codigo_cor),
              id_personalizacao: parseInt(data.id_personalizacao),
              prazo: parseInt(data.prazo),
              quantidade: String(data.quantidade),
              qtd_batida: parseInt(data.qtd_batida),
              nome_personalizacao: data.nome_personalizacao,
              preco_base: formatPrice(data.preco_base),
              preco_site: formatPrice(data.preco_site),
              valor_total_itens: formatPrice(data.valor_total_itens),
              original_valor_total_itens: data.valor_total_itens,
              vlr_substituicao: formatPrice(data.vlr_substituicao),
              original_vlr_substituicao: data.vlr_substituicao,
              preco_venda: formatPrice(data.preco_venda),
              original_preco_venda: data.preco_venda,
              original_preco_base: data.preco_base,
              personalizacao: item.personalizacao,
              cores: item.cores,
              prazos: item.prazos,
              impressoes: item.impressoes,
            }
          }

          return item
        })

        return {
          ...oldData,
          informacao_da_proposta: {
            ...oldData.informacao_da_proposta,
            comissao_prevista: data.comissao_prevista,
            data_saida: data.data_saida,
            vlr_subs: data.vlr_subs,
          },
          itens: updatedItem,
        }
      })

      toast({
        title: `O item ${item.codigo_produto} - ${item.nome_produto} foi atualizado com sucesso!`,
        status: 'success',
      })
    },
  })

  const updateInfoProducts = useCallback(
    async (data) => {
      const qtdUltraRapidoSelecionada = item.cores.find((c) => c.codigo_cor === data.cor).estoque_dia

      setQuantidadeUltraRapidoSelecionada(qtdUltraRapidoSelecionada)

      if (!pendingData && parseInt(data.prazo) <= 2 && data.quantidade > qtdUltraRapidoSelecionada) {
        setPendingData(data)
        setIsAlertMessageQuantityOpen(true)
        return
      }

      try {
        const updatedProduct = {
          id: `${item.codigo_produto}-${parseInt(Math.random() * new Date().getTime())}`,
          valor_unitario: item.original_preco_home,
          valor_total: item.original_preco_home,
          frete_site: null,
          sequencia: item.sequencia,
          codigo_cor: data.cor,
          id_impressao: data.personalizacao,
          qtd_batida: data.impressao,
          prazo: data.prazo,
          codigo_produto: item.codigo_produto,
          quantidade: data.quantidade,
          caricatura_ativa: item.personalizacao.find((personalizacao) => personalizacao.value === data.personalizacao).caricatura || false,
        }

        await mutateAsync(updatedProduct)

        // Reseta o estado após sucesso
        setPendingData(null)
        return updatedProduct
      } catch (error) {}
    },
    [item, mutateAsync, pendingData]
  )

  useEffect(() => {
    if (!recalculateProductPrice) {
      updateInfoProducts({
        cor: item.codigo_cor,
        personalizacao: item.id_personalizacao,
        impressao: String(item.qtd_batida),
        prazo: item.prazo,
        quantidade: String(item.quantidade),
      })
    }
  }, [recalculateProductPrice])

  useEffect(() => {
    setValue('cor', item.codigo_cor)
    setValue('personalizacao', item.id_personalizacao)
    setValue('impressao', String(item.qtd_batida))
    setValue('prazo', item.prazo)
    setValue('quantidade', String(item.quantidade))
  }, [setValue, item])

  const corSelecionada = item.cores.find((c) => c.codigo_cor === cor)

  const dataAtualizar = {
    codigo_produto: item.codigo_produto,
    sequencia: item.sequencia,
    codigo_cor: corSelecionada?.codigo_cor,
    nome_cor: corSelecionada,
    id_personalizacao: personalizacao,
    nome_vendedor: nome_parceiro,
    codigo_vendedor: codigo_vendedor,
    quantidade_produto: watch('quantidade'),
    prazo: prazo,
    numero_nota: item.numero_nota,
    nome_personalizacao: item.personalizacao.find((p) => p.value === personalizacao)?.label || '',
  }

  return (
    <Flex gap="11px" px="13px" ml="auto" as="form" onSubmit={handleSubmit((data) => updateInfoProducts(data))}>
      <ConfirmDialog
        isOpen={isAlertMessageQuantityOpen}
        onClose={() => {
          setIsAlertMessageQuantityOpen(false)
          setPendingData(null)
        }}
        onConfirm={() => {
          setIsAlertMessageQuantityOpen(false)

          if (pendingData) {
            updateInfoProducts(pendingData)
            setPendingData(null)
          }
        }}
        data={dataAtualizar}
        estoqueAtual={quantidadeUltraRapidoSelecionada}
      />
      <Flex flexDir="column" gap={2} flex={1} maxW="309px" w="full" mx="auto" as="fieldset" position="relative" disabled={isLoading}>
        {isLoading && (
          <Flex
            position="absolute"
            w="full"
            h="full"
            bg="rgba(255, 255, 255, 0.5)"
            top="0"
            left="0"
            zIndex="100"
            alignItems="center"
            justifyContent="center"
          >
            <Spinner />
          </Flex>
        )}
        <Select options={item.cores} placeholder="Selecione uma cor" name="cor" error={errors.cor} control={control} />
        <Select
          maxW="309px"
          placeholder="Selecione uma Personalização"
          options={item.personalizacao}
          name="personalizacao"
          error={errors.personalizacao}
          control={control}
        />
        {/* <Select options={item.impressoes} control={control} name="impressao" /> */}
        <Flex alignItems="center" gap={2}>
          <InputGroup>
            <InputLeftAddon children="Impressões" fontSize="12px" h="29px" bgColor="#f5f5f5" borderRadius="0" borderColor="#CFCFCF" />
            <Input
              borderRadius={0}
              borderColor={errors.impressao ? '#FF0000' : '#CFCFCF'}
              h="29px"
              size="sm"
              maxW="309px"
              placeholder="Digite a impressão"
              {...register('impressao')}
            />
          </InputGroup>
        </Flex>
        <Select options={item.prazos} placeholder="Selecione um Prazo" name="prazo" error={errors.prazo} control={control} />
        <Flex alignItems="center">
          <InputGroup>
            <InputLeftAddon children="Quantidade" fontSize="12px" h="29px" bgColor="#f5f5f5" borderRadius="0" borderColor="#CFCFCF" />
            <Input
              borderRadius={0}
              h="29px"
              size="sm"
              maxW="309px"
              placeholder="Digite a quantidade"
              borderColor={errors.quantidade ? '#FF0000' : '#CFCFCF'}
              {...register('quantidade')}
            />
          </InputGroup>

          {item.multiplo !== 0 && (
            <Multiplo
              personalizacao={personalizacao}
              impressao={impressao}
              prazo={prazo}
              cor={corSelecionada}
              atualizar={handleSubmit((data) => {
                updateInfoProducts(data)
              })}
              item={item}
              setValue={setValue}
            />
          )}
          {item.multiplo === 0 && (
            <Button type="submit" bgColor="#0075B1" isLoading={isLoading} h="29px" borderRadius="0">
              Atualizar
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}
